import { OfferStatus } from "~/lib/schemas/db-enums";
import { toExternalHref } from "~/lib/shared/link-rel";

/**
 * Resolve the single primary call-to-action for an offer.
 * "buy" → direct Stripe checkout; "apply" → contact/booking link.
 * The one source of truth for offer CTA mode across hero, sections,
 * pricing block, and sticky bar.
 *
 * Every variant carries the analytics context (offerId, offerType,
 * coachUsername) so the rendering button can fire an attributable
 * `cta_button_clicked` event without each section threading extra props.
 * Aliases: offer cta, offer mode, buy vs contact.
 */
type CtaAnalytics = {
  offerId: string;
  offerType: string;
  coachUsername: string;
};

export type CtaDescriptor =
  | ({ kind: "buy"; label: string } & CtaAnalytics)
  | ({
      kind: "apply";
      label: string;
      href: string;
      ugc?: boolean;
    } & CtaAnalytics);

export function pickOfferCta(args: {
  offer: {
    id: string;
    type: string;
    status: OfferStatus;
    directSaleEnabled: boolean | null;
    price: number | null;
    ctaUrl?: string | null;
  };
  coach: {
    username: string;
    profile: {
      ctaButtonText: string | null;
      contactMode: "booking" | "messaging" | null;
    };
  };
}): CtaDescriptor {
  const { offer, coach } = args;
  const analytics: CtaAnalytics = {
    offerId: offer.id,
    offerType: offer.type,
    coachUsername: coach.username,
  };
  // Only a published offer can be bought. A coach previewing their draft sees
  // the contact action rather than a checkout that would refuse the purchase.
  if (
    offer.status === OfferStatus.Published &&
    offer.directSaleEnabled &&
    (offer.price ?? 0) > 0
  ) {
    return { kind: "buy", label: "Buy now →", ...analytics };
  }

  const customText = coach.profile.ctaButtonText?.trim();
  const customHref = offer.ctaUrl?.trim();
  const inBookingMode = coach.profile.contactMode === "booking";
  // A calendar URL on the offer is an explicit booking action, as is the
  // coach's booking contact mode.
  const isBooking = Boolean(customHref) || inBookingMode;

  // The coach's custom button text is tied to their default contact mode, so
  // it only fits the label when the resolved action matches. Otherwise a
  // messaging label ("Send Message") would ride on a booking link — the bug
  // this guards against: filling in a calendar URL must read as "book".
  const label = isBooking
    ? inBookingMode && customText
      ? customText
      : "Book a call"
    : customText || "Start a conversation";

  if (customHref) {
    return {
      kind: "apply",
      label,
      href: toExternalHref(customHref),
      ugc: true,
      ...analytics,
    };
  }
  const href = inBookingMode
    ? `/${coach.username}/book`
    : `/${coach.username}/contact`;
  return { kind: "apply", label, href, ...analytics };
}
